import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { errorMessage, isApiError } from '@/core/http';
import { Button, Dialog, Field, Input, Textarea } from '@/core/ui';
import { useCreateCourse } from '../application/queries';
import {
  collapseSpaces,
  createCourseSchema,
  formatTags,
  LIMITS,
  parseTags,
  type CreateCourseForm,
} from '../domain/schemas';
import { formatted } from './formatted';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (courseId: string) => void;
}

const EMPTY: CreateCourseForm = { title: '', description: '', tags: '' };

/**
 * Novo curso: nasce como rascunho e abre direto no editor. O endereço no
 * portal sai do título — não é escolhido aqui.
 */
export function CreateCourseDialog({ open, onClose, onCreated }: Props) {
  const create = useCreateCourse();
  const form = useForm<CreateCourseForm>({
    resolver: zodResolver(createCourseSchema),
    defaultValues: EMPTY,
    mode: 'onTouched',
  });
  const [description, tags] = useWatch({ control: form.control, name: ['description', 'tags'] });
  const { errors } = form.formState;
  const duplicate = isApiError(create.error) && create.error.is('duplicate_slug');
  const tagCount = parseTags(tags).length;

  const close = () => {
    form.reset(EMPTY);
    create.reset();
    onClose();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const course = await create.mutateAsync(values);
      form.reset(EMPTY);
      onCreated(course.id);
    } catch (e) {
      if (isApiError(e) && e.is('duplicate_slug'))
        form.setError('title', { message: 'Já existe um curso com este título' });
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Novo curso"
      description="Nasce como rascunho. Publique quando as aulas estiverem prontas."
      className="sm:max-w-lg"
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label="Título" error={errors.title?.message}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              maxLength={LIMITS.courseTitle.max}
              placeholder="Ex.: Tireoide na prática"
              {...formatted(form, 'title', { on: 'blur', apply: collapseSpaces })}
            />
          )}
        </Field>
        <Field
          label="Descrição (opcional)"
          error={errors.description?.message}
          hint={`${description.length.toLocaleString('pt-BR')} de ${LIMITS.description.toLocaleString('pt-BR')}`}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              maxLength={LIMITS.description}
              placeholder="Para quem é e o que se aprende"
              {...form.register('description')}
            />
          )}
        </Field>
        <Field
          label="Tags (opcional)"
          error={errors.tags?.message}
          hint={
            tagCount > 0
              ? `${tagCount} de ${LIMITS.tags} · separadas por vírgula`
              : 'Separadas por vírgula. Ex.: vendas, onboarding'
          }
        >
          {(p) => <Input {...p} placeholder="vendas, onboarding" {...formatted(form, 'tags', { on: 'blur', apply: formatTags })} />}
        </Field>

        {create.isError && !duplicate && (
          <p role="alert" className="rounded-lg border border-line-strong px-3 py-2 text-sm text-fg">
            {errorMessage(create.error)}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" loading={create.isPending}>
            Criar curso
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
